import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle2, XCircle, Clock, ArrowRight, User, Building2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_LABELS = {
  name: 'Account Name', address: 'Address', city: 'City',
  pincode: 'Pincode', current_sales_value: 'Sales Value',
  whatsapp_number: 'Phone Number', designation: 'Designation',
  can_influence_decisions: 'Can Influence Decisions', email: 'Email',
  contact_name: 'Contact Name',
};

const Approvals = ({ onLogout }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(null);

  useEffect(() => { fetchRequests(); }, [activeTab]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/change-requests?status=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch {
      toast.error('Failed to load change requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/change-requests/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Change approved and applied!');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/change-requests/${id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Change request rejected');
      setRejectingId(null);
      setRejectionReason('');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const pendingCount = activeTab === 'pending' ? requests.length : 0;

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Approvals
            </h1>
            <p className="text-base text-slate-500 mt-2">
              Review and approve account changes submitted by your team
            </p>
          </div>
          <Button
            onClick={fetchRequests}
            variant="outline"
            className="rounded-xl"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'pending', label: 'Pending', icon: Clock, color: 'amber' },
            { key: 'approved', label: 'Approved', icon: CheckCircle2, color: 'emerald' },
            { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'red' },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  isActive
                    ? `bg-${tab.color}-100 text-${tab.color}-700 border-2 border-${tab.color}-300`
                    : 'bg-white text-slate-500 border-2 border-slate-200 hover:border-slate-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.key === 'pending' && requests.length > 0 && activeTab === 'pending' && (
                  <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {requests.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent" />
            <p className="text-slate-500 mt-4">Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-20 bg-white/80 rounded-2xl border border-slate-200">
            <CheckCircle2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">
              {activeTab === 'pending' ? 'No pending approvals — all clear! ✅' : `No ${activeTab} requests`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map(req => (
              <div
                key={req.id}
                className={`bg-white/80 backdrop-blur-xl rounded-2xl border shadow-md p-6 ${
                  req.status === 'pending' ? 'border-amber-200' :
                  req.status === 'approved' ? 'border-emerald-200' : 'border-red-200'
                }`}
              >
                {/* Header row */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${
                      req.status === 'pending' ? 'bg-amber-100' :
                      req.status === 'approved' ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      <Building2 className={`h-5 w-5 ${
                        req.status === 'pending' ? 'text-amber-600' :
                        req.status === 'approved' ? 'text-emerald-600' : 'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg">{req.account_name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                        <User className="h-3 w-3" />
                        {req.requested_by_name} · {formatDate(req.requested_at)}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
                    req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {req.status === 'pending' ? '🕐 Pending' :
                     req.status === 'approved' ? '✅ Approved' : '❌ Rejected'}
                  </span>
                </div>

                {/* Field changes */}
                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    {req.change_type === 'contact' ? 'Contact Changes' : 'Account Changes'}
                  </p>
                  <div className="space-y-2">
                    {Object.entries(req.field_changes || {}).map(([field, change]) => (
                      <div key={field} className="flex items-center gap-3 text-sm">
                        <span className="text-slate-500 w-32 font-medium flex-shrink-0">
                          {FIELD_LABELS[field] || field}
                        </span>
                        <span className="text-red-500 line-through bg-red-50 px-2 py-0.5 rounded flex-1 truncate">
                          {change.old_value || '(empty)'}
                        </span>
                        <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded flex-1 truncate">
                          {change.new_value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rejection reason (if rejected) */}
                {req.status === 'rejected' && req.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                    <p className="text-sm text-red-700">
                      <span className="font-semibold">Rejection reason:</span> {req.rejection_reason}
                    </p>
                    <p className="text-xs text-red-500 mt-1">
                      Reviewed by {req.reviewed_by} on {formatDate(req.reviewed_at)}
                    </p>
                  </div>
                )}

                {/* Approved info */}
                {req.status === 'approved' && (
                  <p className="text-xs text-emerald-600 mb-4">
                    ✅ Approved by {req.reviewed_by} on {formatDate(req.reviewed_at)}
                  </p>
                )}

                {/* Action buttons (pending only) */}
                {req.status === 'pending' && (
                  <div>
                    {rejectingId === req.id ? (
                      <div className="flex gap-3 items-center">
                        <Input
                          value={rejectionReason}
                          onChange={e => setRejectionReason(e.target.value)}
                          placeholder="Reason for rejection (optional)..."
                          className="flex-1 h-10 rounded-xl"
                        />
                        <Button
                          onClick={() => handleReject(req.id)}
                          disabled={processing === req.id}
                          className="h-10 bg-red-600 hover:bg-red-700 text-white rounded-xl"
                        >
                          {processing === req.id ? 'Rejecting...' : 'Confirm Reject'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                          className="h-10 rounded-xl"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleApprove(req.id)}
                          disabled={processing === req.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-6"
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          {processing === req.id ? 'Approving...' : 'Approve'}
                        </Button>
                        <Button
                          onClick={() => setRejectingId(req.id)}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl px-6"
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Approvals;
